import React, { useEffect, useMemo, useRef, useState } from 'react';
import SlidePanel from '../../shared/SlidePanel';
import { clientDuplicatesService } from '../../../_services/clientDuplicates.service';
import {
    SIGNAL_LABEL, PICK_FIELDS, defaultPicks, fieldOptions, differingFields, emailLocked,
    picksToValues, recordsIn, timesOnHere, refusalCopy, plural,
} from './duplicateCopy';
import '../ClientDetailPanel.css';
import './Duplicates.css';

const MAX_PER_REQUEST = 20; // BE §C v1 limit; bigger selections go in turns

const newKey = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `dup-${Date.now()}-${Math.random().toString(36).slice(2)}`);

function contactLine(m) {
    return [m.email, m.phoneNumber].filter(Boolean).join(' · ') || 'No contact details';
}

function MemberCard({ member, kept, onKeep, readOnly }) {
    // A dismissed pair isn't being merged: no keep choice, just who they are.
    if (readOnly) {
        return (
            <div className="dup-card">
                <div className="dup-card-name">{member.name}</div>
                <div className="dup-card-contact">{contactLine(member)}</div>
            </div>
        );
    }
    return (
        <button
            type="button"
            className={`dup-card${kept ? ' is-kept' : ''}`}
            onClick={onKeep}
            aria-pressed={kept}
            aria-label={kept ? `${member.name}, kept as the main record` : `Keep ${member.name} as the main record`}
        >
            <div className="dup-card-row">
                <span className="dup-radio" aria-hidden="true" />
                <div style={{ minWidth: 0 }}>
                    <div className="dup-card-name">{member.name}</div>
                    <div className="dup-card-contact">{contactLine(member)}</div>
                </div>
            </div>
            <div className="dup-card-meta">
                {plural(member.bookingCount, 'booking')}
                {member.hasLogin ? ' · uses the Beu app' : ''}
                {kept ? <> · <span className="dup-card-kept">Kept as the main record</span></> : null}
            </div>
        </button>
    );
}

function GroupDetails({ group, keepUserId, picks, onPick, movesEverything }) {
    const locked = emailLocked(group, keepUserId);
    const keep = group.members.find((m) => m.userId === keepUserId);
    return (
        <div className="dup-details">
            {PICK_FIELDS.map(({ key, label }) => {
                if (key === 'email' && locked) {
                    return (
                        <div className="dup-field" key={key}>
                            <div className="dup-field-label">{label}</div>
                            <div className="dup-field-value">{keep.email} <span className="dup-field-note">· their login email, only they can change it</span></div>
                        </div>
                    );
                }
                const opts = fieldOptions(group, key);
                if (!opts.length) return null;
                if (opts.length === 1) {
                    return (
                        <div className="dup-field" key={key}>
                            <div className="dup-field-label">{label}</div>
                            <div className="dup-field-value">{opts[0].value}</div>
                        </div>
                    );
                }
                const chosen = group.members.find((m) => m.userId === picks[key]);
                const chosenValue = chosen && chosen[key] ? chosen[key].trim().toLowerCase() : '';
                return (
                    <div className="dup-field" key={key} role="radiogroup" aria-label={label}>
                        <div className="dup-field-label">{label}</div>
                        <div className="dup-options">
                            {opts.map((o) => {
                                const on = o.value.toLowerCase() === chosenValue;
                                return (
                                    <button type="button" key={o.value} role="radio" aria-checked={on}
                                        className={`dup-option${on ? ' is-on' : ''}`} onClick={() => onPick(key, o.userId)}>
                                        <span className="dup-radio" aria-hidden="true" />{o.value}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            <div className="dup-details-foot">
                Notes from {group.members.length === 2 ? 'both records' : `all ${group.members.length} records`} are kept. {movesEverything ? 'Bookings, photos, forms, loyalty and gift cards all move' : 'Bookings move'} to {keep ? keep.name : 'the kept record'}.
            </div>
        </div>
    );
}

/**
 * Review duplicates (mockup screens 2 + 2b). Strong matches come ticked,
 * possible ones unticked; the survivor and details are pre-picked (§E) and
 * every pick can be changed. Merging goes in turns of 20 groups.
 */
// mergeOff: salon merges are switched off (paused, or a read-only pilot): the
// list is look-only, with no merge and no "Not a duplicate" (both write).
export default function DuplicatesReviewPanel({ initial, onClose, onMerged, mergeOff = false }) {
    const [movesEverything, setMovesEverything] = useState(!!(initial && initial.movesEverything));
    const panelRef = useRef(null);
    const [groups, setGroups] = useState(initial ? initial.groups : []);
    const [loading, setLoading] = useState(!initial);
    const [loadFailed, setLoadFailed] = useState(false);
    const [selected, setSelected] = useState({});
    const [keep, setKeep] = useState({});
    const [picks, setPicks] = useState({});
    const [open, setOpen] = useState({});
    const [merging, setMerging] = useState(false);
    const [notice, setNotice] = useState(null);
    const [showDismissed, setShowDismissed] = useState(false);
    const [dismissed, setDismissed] = useState([]);

    const seed = (list) => {
        const sel = {}, kp = {}, pk = {};
        list.forEach((g) => {
            sel[g.groupKey] = g.confidence === 'strong' && !g.labels.includes('different_surname');
            kp[g.groupKey] = g.keepUserId;
            pk[g.groupKey] = defaultPicks(g, g.keepUserId);
        });
        setSelected(sel); setKeep(kp); setPicks(pk);
    };

    useEffect(() => {
        if (initial) { seed(initial.groups); return; }
        clientDuplicatesService.list()
            .then((d) => { setGroups(d.groups); setMovesEverything(d.movesEverything); seed(d.groups); setLoading(false); })
            .catch(() => { setLoadFailed(true); setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const close = () => { if (panelRef.current) panelRef.current.handleClose(); else onClose(); };

    const chosen = useMemo(() => groups.filter((g) => selected[g.groupKey]), [groups, selected]);
    const toMerge = recordsIn(chosen);
    const allOn = groups.length > 0 && chosen.length === groups.length;

    const setAll = (on) => {
        const sel = {};
        groups.forEach((g) => { sel[g.groupKey] = on; });
        setSelected(sel);
    };

    const chooseKeep = (g, userId) => {
        if (keep[g.groupKey] === userId) return;
        setKeep({ ...keep, [g.groupKey]: userId });
        setPicks({ ...picks, [g.groupKey]: defaultPicks(g, userId) });
    };

    const dismiss = (g) => {
        const before = groups;
        setGroups(groups.filter((x) => x.groupKey !== g.groupKey));
        setNotice(null);
        clientDuplicatesService.dismiss(g)
            .then(() => setDismissed((d) => [{ ...g, dismissedAt: new Date().toISOString() }, ...d]))
            .catch(() => { setGroups(before); setNotice(refusalCopy('invalid').body); });
    };

    const toggleDismissed = () => {
        const next = !showDismissed;
        setShowDismissed(next);
        if (next) {
            clientDuplicatesService.list({ dismissed: true })
                .then((d) => setDismissed(d.groups))
                .catch(() => setDismissed([]));
        }
    };

    const restore = (g) => {
        setDismissed(dismissed.filter((x) => x.groupKey !== g.groupKey));
        clientDuplicatesService.restore(g)
            // A dismissed pair carries names only: reload so it comes back whole.
            .then(() => clientDuplicatesService.list({ fresh: true }))
            .then((d) => {
                const back = d.groups.find((x) => x.members.some((m) => g.members.some((gm) => gm.userId === m.userId)));
                if (!back) return;
                setGroups((list) => [...list.filter((x) => x.groupKey !== back.groupKey), back]);
                setSelected((s) => ({ ...s, [back.groupKey]: false }));
                setKeep((k) => ({ ...k, [back.groupKey]: back.keepUserId }));
                setPicks((p) => ({ ...p, [back.groupKey]: defaultPicks(back, back.keepUserId) }));
            })
            .catch(() => setDismissed((d) => [g, ...d]));
    };

    const doMerge = async () => {
        if (!chosen.length || merging) return;
        setMerging(true);
        setNotice(null);
        const payload = chosen.map((g) => {
            const k = keep[g.groupKey];
            return {
                groupKey: g.groupKey,
                keepUserId: k,
                mergeUserIds: g.members.map((m) => m.userId).filter((id) => id !== k),
                picks: picksToValues(g, picks[g.groupKey] || {}, k),
            };
        });
        const results = [];
        const batchIds = [];
        let stopCode = null;
        for (let i = 0; i < payload.length; i += MAX_PER_REQUEST) {
            try {
                const out = await clientDuplicatesService.merge(payload.slice(i, i + MAX_PER_REQUEST), newKey());
                if (out.batchId) batchIds.push(out.batchId);
                results.push(...out.results);
            } catch (e) {
                stopCode = e.code || 'invalid';
                break;
            }
        }
        setMerging(false);
        const okKeys = new Set(results.filter((r) => r.ok).map((r) => r.groupKey));
        if (!okKeys.size) {
            const code = stopCode || (results.find((r) => !r.ok) || {}).code || 'invalid';
            setNotice(refusalCopy(code).body);
            return;
        }
        setGroups(groups.filter((g) => !okKeys.has(g.groupKey)));
        if (onMerged) onMerged({ batchIds, results, stopCode });
        close();
    };

    const renderGroup = (g, { dismissedView } = {}) => {
        const k = keep[g.groupKey] || g.keepUserId;
        const on = !!selected[g.groupKey];
        const diff = differingFields(g).filter((f) => !(f === 'email' && emailLocked(g, k)));
        const why = g.members.length > 2 ? `${g.members.length} records · ${(SIGNAL_LABEL[g.signal] || 'Same details').toLowerCase()}` : (SIGNAL_LABEL[g.signal] || 'Same details');
        return (
            <div key={g.groupKey} className={`dup-group${!on && !dismissedView ? ' is-off' : ''}`}>
                <div className="dup-group-head">
                    {!dismissedView && (
                        <input type="checkbox" checked={on} aria-label={`Include ${g.members[0] ? g.members[0].name : 'this group'}`}
                            onChange={(e) => setSelected({ ...selected, [g.groupKey]: e.target.checked })} />
                    )}
                    <span className="dup-why">{why}</span>
                    {g.confidence === 'possible' && <span className="dup-tag">Possible match</span>}
                    {g.labels.includes('different_surname') && <span className="dup-tag">Different surname</span>}
                    <div className="dup-group-actions">
                        {dismissedView ? (
                            mergeOff ? null : <button type="button" className="dup-link" onClick={() => restore(g)}>Show again</button>
                        ) : (
                            <>
                                {diff.length > 0 && (
                                    <button type="button" className="dup-link" aria-expanded={!!open[g.groupKey]}
                                        onClick={() => setOpen({ ...open, [g.groupKey]: !open[g.groupKey] })}>
                                        {open[g.groupKey] ? 'Hide details' : 'Check details'}
                                    </button>
                                )}
                                {!mergeOff && <button type="button" className="dup-link dup-link--muted" onClick={() => dismiss(g)}>Not a duplicate</button>}
                            </>
                        )}
                    </div>
                </div>
                <div className={`dup-cards${g.members.length > 2 ? ' dup-cards--stack' : ''}`}>
                    {g.members.map((m) => (
                        <MemberCard key={m.userId} member={m} kept={m.userId === k} readOnly={dismissedView} onKeep={() => chooseKeep(g, m.userId)} />
                    ))}
                </div>
                {!dismissedView && open[g.groupKey] && (
                    <GroupDetails group={g} keepUserId={k} picks={picks[g.groupKey] || {}} movesEverything={movesEverything}
                        onPick={(field, userId) => setPicks({ ...picks, [g.groupKey]: { ...(picks[g.groupKey] || {}), [field]: userId } })} />
                )}
            </div>
        );
    };

    return (
        <SlidePanel ref={panelRef} width={600} onClose={onClose} className="cdp-panel">
            <div className="cdp-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <button className="beu-icon-btn" onClick={close} title="Back to clients" aria-label="Back to clients">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <h2 className="cdp-header-name">Review duplicates</h2>
                </div>
            </div>

            <div className="dup-body">
                {loading && <><p className="dup-intro">Checking your clients for anyone who's on here more than once…</p><div className="dup-skeleton" /><div className="dup-skeleton" /><div className="dup-skeleton" /></>}
                {loadFailed && <div className="dup-empty">We couldn't load the duplicates just now. Close this and try again in a moment.</div>}

                {!loading && !loadFailed && (
                    <>
                        {notice && (
                            <div className="dup-note" role="status">
                                <div className="dup-note-body">{notice}</div>
                            </div>
                        )}
                        {groups.length > 0 ? (
                            <>
                                <p className="dup-intro">
                                    {groups.length === 1 ? `This client is on here ${timesOnHere(groups[0].members.length)}.` : `${groups.length} clients are on here more than once.`} For each one we've picked the record to keep. Tap another card to switch. You can undo any merge within 72 hours.
                                </p>
                                <div className="dup-section-head">
                                    <div className="dup-section-label">{plural(groups.length, 'match', 'matches')}</div>
                                    <label className="dup-check-label">
                                        <input type="checkbox" checked={allOn} onChange={(e) => setAll(e.target.checked)} /> Select all
                                    </label>
                                </div>
                                {groups.map((g) => renderGroup(g))}
                                {movesEverything && (
                                    <div className="dup-note">
                                        <div className="dup-note-title">Nothing is lost</div>
                                        <div className="dup-note-body">Bookings, notes, photos, forms, loyalty and gift cards move onto the kept record. Every stylist who looks after the client keeps them. Clients who use the Beu app keep their login.</div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="dup-empty">No duplicates to review. New ones show up here when two clients share an email or phone number.</div>
                        )}

                        <div className="dup-section-head">
                            <button type="button" className="dup-link dup-link--muted" onClick={toggleDismissed} aria-expanded={showDismissed}>
                                {showDismissed ? 'Hide dismissed' : 'Show dismissed'}
                            </button>
                        </div>
                        {showDismissed && (dismissed.length
                            ? dismissed.map((g) => renderGroup(g, { dismissedView: true }))
                            : <div className="dup-empty" style={{ padding: '8px' }}>Nothing dismissed.</div>)}
                    </>
                )}
            </div>

            <div className="dup-footer">
                {mergeOff ? (
                    <>
                        <span className="dup-footer-note">Merging is switched off for now, so you can look but not merge.</span>
                        <button type="button" className="cdp-btn-cancel" onClick={close}>Close</button>
                    </>
                ) : (
                    <>
                        <button type="button" className="cdp-btn-cancel" onClick={close} disabled={merging}>Cancel</button>
                        <button type="button" className="cdp-btn-save" onClick={doMerge} disabled={merging || !chosen.length}>
                            {merging ? 'Merging…' : (toMerge ? `Merge ${plural(toMerge, 'record')}` : 'Merge')}
                        </button>
                    </>
                )}
            </div>
        </SlidePanel>
    );
}
